import React from 'react'

import * as authActions from '../../actions/auth'
import * as modalActions from '../../actions/modal'

import {HttpStatusError} from '../../../../commons/src/libs/http-json'
import {captureException, captureIgnoredException} from '../../../../commons/src/services/logging'
import useDispatch from '../../hooks/use-dispatch'
import useSelector from '../../hooks/use-selector'
import {getOrder, patchOrderStatus} from '../../services/api/partner'

import plusIcon from '../../../../commons/src/assets/icons/plus.svg'
import truckIcon from '../../../../commons/src/assets/icons/truck.svg'

import ActionBar from '../../../../commons/src/components/action-bar/ActionBar'
import Icon from '../../../../commons/src/components/icon'
import LinkButton from '../../../../commons/src/components/link-button/LinkButton'
import Paragraph from '../../../../commons/src/components/paragraph'
import Stack from '../../../../commons/src/components/stack'
import TruncatedLabel from '../../../../commons/src/components/truncated-label/TruncatedLabel'

import {Order, OrderStatusType} from '../../order-types'

class OrderTrackingUpdateError extends Error {
  constructor(message?: string) {
    super(message || `Updating order tracking number failed`)
    this.name = 'OrderTrackingUpdateError'
  }
}

export interface Props {
  order: Order
  onOrderUpdate: (updatedOrder: Order) => void
}

const OrderTrackingPartial = ({order, onOrderUpdate}: Props) => {
  const dispatch = useDispatch()

  const provider = useSelector(state => state.core.provider)

  const updateOrderStatus = async (trackingNumber?: string) => {
    await dispatch(modalActions.openLoadingModal('Updating order status...'))
    try {
      await patchOrderStatus(order.orderId, {status: order.printingService.status, trackingNumber})
      const updatedOrder = await getOrder(order.orderId)
      onOrderUpdate(updatedOrder)

      dispatch(modalActions.close())
    } catch (error) {
      if (error && error instanceof HttpStatusError && error.response.status === 401) {
        captureIgnoredException(error)

        dispatch(modalActions.close())
        dispatch(authActions.signOut())
      } else {
        captureException(new OrderTrackingUpdateError((error as Error)?.message), 'default', {
          originalError: error,
          orderNumber: order.orderNumber,
          providerId: provider?.id,
          status: order.printingService.status,
          trackingNumber,
        })
        dispatch(
          modalActions.openSimpleContentModal(
            'Order Tracking Number Update Error',
            <Paragraph>
              Oops something went wrong. Please try again and contact us if the problem persists.
            </Paragraph>,
            true
          )
        )
      }
    }
  }

  const handleOnClick = () => {
    dispatch(
      modalActions.openAddTrackingNumberModal(order, (trackingNumber?: string) => {
        updateOrderStatus(trackingNumber)
      })
    )
  }

  return (
    <Stack space="60">
      {order.printingService.trackingNumber ? (
        <ActionBar>
          <TruncatedLabel size="s" maxWidth>
            <Icon source={truckIcon} /> {order.printingService.trackingNumber}
          </TruncatedLabel>
          {order.printingService.status !== OrderStatusType.CANCELLED && (
            <LinkButton onClick={handleOnClick} icon={<Icon source={plusIcon} />}>
              Add/Review
            </LinkButton>
          )}
        </ActionBar>
      ) : (
        <ActionBar>
          <TruncatedLabel size="s" maxWidth minor>
            <Icon source={truckIcon} /> no tracking number
          </TruncatedLabel>
          {order.printingService.status !== OrderStatusType.CANCELLED && (
            <LinkButton onClick={handleOnClick} icon={<Icon source={plusIcon} />}>
              Add
            </LinkButton>
          )}
        </ActionBar>
      )}
      {order.printingService.trackingUrl && (
        <TruncatedLabel size="s" maxWidth minor>
          {order.printingService.trackingUrl}
        </TruncatedLabel>
      )}
    </Stack>
  )
}

export default OrderTrackingPartial
